/**
 * 紧凑项目卡片组件
 * 用于在Dashboard中以更高密度显示项目信息
 * 设计理念: 信息聚焦、状态清晰、操作直接
 */
import React from 'react';
import { Card, Progress, Tag, Typography, Space, Tooltip } from 'antd';
import {
  SyncOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  PauseCircleOutlined,
  ArrowRightOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import type { ProjectOverviewData } from '../../services/dashboardService';
import { DashboardService } from '../../services/dashboardService';
import { getBudgetBasedColors } from '../../utils/colorUtils';

const { Text } = Typography;

const CARD_THEME = {
  // 统一配色系统
  background: 'rgba(224, 230, 234, 0.82)',
  border: '1px solid rgba(16, 27, 35, 0.08)',
  panelBackground: 'rgba(255, 255, 255, 0.05)',
  panelBorder: '1px solid rgba(16, 27, 35, 0.08)',
  title: '#10171d',
  body: '#364954',
  muted: '#667986',
  weak: '#667986',
  shadow: '0 20px 48px rgba(46, 60, 70, 0.12)',
  accentGold: '#b99258',
  accentBlue: '#547590',
  // 面板内使用更轻的背景
  panelInnerBg: 'rgba(232, 237, 240, 0.86)',
  panelInnerBorder: '1px solid rgba(16, 27, 35, 0.08)',
};

export interface CompactProjectCardProps {
  project: ProjectOverviewData;
  onClick?: (project: ProjectOverviewData) => void;
  isMobile?: boolean;
}

const STATUS_CONFIG: Record<string, { color: string; icon: React.ReactNode }> = {
  '进行中': { color: 'processing', icon: <SyncOutlined spin /> },
  '已完成': { color: 'success', icon: <CheckCircleOutlined /> },
  '待开始': { color: 'default', icon: <ExclamationCircleOutlined /> },
  '已暂停': { color: 'warning', icon: <PauseCircleOutlined /> }
};

export const CompactProjectCard: React.FC<CompactProjectCardProps> = ({
  project,
  onClick,
  isMobile = false
}) => {
  const health = DashboardService.calculateProjectHealth(project.budget, project.actualCost);
  const statusConfig = STATUS_CONFIG[project.status] || STATUS_CONFIG['待开始'];
  const budgetColors = getBudgetBasedColors(project.budget, project.actualCost);
  const usagePercent = project.budget > 0 ? (project.actualCost / project.budget) * 100 : 0;
  const projectCode = ((project as any).projectCode || 'UNSET').toString();
  const riskTag = project.riskTags?.[0] || '预算健康';
  const usageLabel = project.budget > 0 ? `${usagePercent.toFixed(1)}%` : '未设预算';

  const formatMoney = (value: number): string => {
    if (value >= 10000) {
      return `¥${(value / 10000).toFixed(1)}万`;
    }
    return `¥${value.toLocaleString()}`;
  };

  const getBudgetTooltip = () => {
    if (budgetColors.status === 'unset') {
      return '未设置预算';
    }
    return `预算使用率: ${budgetColors.percentage.toFixed(1)}% (${budgetColors.statusText})`;
  };

  return (
    <Card
      hoverable
      size="small"
      onClick={() => onClick?.(project)}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',
        borderRadius: 20,
        overflow: 'hidden',
        border: CARD_THEME.border,
        background: CARD_THEME.background,
        boxShadow: CARD_THEME.shadow,
        width: '100%',
        minWidth: 0,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
      }}
      styles={{
        body: {
          padding: isMobile ? '12px 14px' : '14px 16px',
          width: '100%',
          boxSizing: 'border-box'
        }
      }}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {/* 头部区域 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          {/* 左侧项目信息 */}
          <div style={{ minWidth: 0, flexShrink: 1, flexGrow: 1 }}>
            {/* 项目编码标签 */}
            <div
              style={{
                display: 'inline-block',
                color: CARD_THEME.accentBlue,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.12em',
                background: 'rgba(84, 117, 144, 0.1)',
                padding: '2px 8px',
                borderRadius: 6,
                marginBottom: 6,
                textTransform: 'uppercase'
              }}
            >
              {projectCode}
            </div>
            {/* 项目名称 */}
            <div
              style={{
                color: CARD_THEME.title,
                fontSize: isMobile ? 15 : 16,
                fontWeight: 700,
                lineHeight: 1.35,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                paddingLeft: 10,
                borderLeft: `3px solid ${CARD_THEME.accentGold}`,
                marginLeft: -10,
                paddingRight: 4
              }}
            >
              <Tooltip title={project.name} placement="topLeft">
                <span style={{ cursor: 'help' }}>{project.name}</span>
              </Tooltip>
            </div>
          </div>
          {/* 状态标签 */}
          <Tag
            color={statusConfig.color}
            icon={statusConfig.icon}
            style={{
              marginRight: 0,
              fontSize: 11,
              fontWeight: 600,
              borderRadius: 999,
              paddingInline: 8,
              lineHeight: '22px',
              background: statusConfig.color === 'processing' ? 'rgba(84, 117, 144, 0.14)' :
                         statusConfig.color === 'success' ? 'rgba(101, 214, 184, 0.14)' :
                         statusConfig.color === 'warning' ? 'rgba(246, 196, 83, 0.14)' :
                         'rgba(102, 121, 134, 0.12)',
              border: '1px solid rgba(16, 27, 35, 0.08)'
            }}
          >
            {project.status}
          </Tag>
        </div>

        {/* 预算信息区块 */}
        <Tooltip title={getBudgetTooltip()} placement="top">
          <div
            style={{
              padding: 12,
              borderRadius: 14,
              background: CARD_THEME.panelInnerBg,
              border: CARD_THEME.panelInnerBorder
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text style={{ color: CARD_THEME.body, fontSize: 12, fontWeight: 600 }}>预算燃耗</Text>
              <Text style={{ color: health.color, fontSize: 13, fontWeight: 700 }}>{usageLabel}</Text>
            </div>
            <Progress
              percent={Math.min(budgetColors.percentage, 100)}
              size="small"
              strokeColor={budgetColors.percentage >= 100 ? '#ff7875' : budgetColors.percentage >= 80 ? '#ffd666' : '#5eead4'}
              trailColor="rgba(16, 27, 35, 0.08)"
              style={{ marginBottom: 10 }}
              showInfo={false}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ color: CARD_THEME.muted, fontSize: 11, marginBottom: 2 }}>成本</div>
                <div style={{ color: CARD_THEME.title, fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>
                  {formatMoney(project.actualCost)}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: CARD_THEME.muted, fontSize: 11, marginBottom: 2 }}>预算</div>
                <div style={{ color: CARD_THEME.title, fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>
                  {project.budget > 0 ? formatMoney(project.budget) : '未设置'}
                </div>
              </div>
            </div>
          </div>
        </Tooltip>

        {/* 底部操作区 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <Space size={6}>
            <ThunderboltOutlined style={{ color: riskTag === '预算健康' ? '#65d6b8' : '#fcd34d', fontSize: 12 }} />
            <Text style={{ color: CARD_THEME.body, fontSize: 12, fontWeight: 600 }}>
              {riskTag}
            </Text>
          </Space>
          <Space size={4}>
            <Text style={{ color: CARD_THEME.muted, fontSize: 11, fontWeight: 600 }}>查看</Text>
            <ArrowRightOutlined style={{ color: CARD_THEME.accentBlue, fontSize: 11 }} />
          </Space>
        </div>
      </Space>
    </Card>
  );
};

export default CompactProjectCard;
